import type { QueryHookOptions } from "@apollo/client";
import { useQuery, gql } from "@apollo/client";

import type {
  MerchantData,
  StoreMetrics,
  StoreAdditionalDiscount,
  StripeAccountPersonType,
  StripeAdditionalVerificationStates,
} from "@shared/types/graphqlGenerated";
import type { MarginType } from "@shared/types/marginTypes";

const PayoutDetails_Shared_Query = gql`
  query PayoutDetails_Shared_Query {
    currentPractitioner {
      id
      properties {
        id
        isStudent
      }
    }
    currentStore {
      id
      profitMargin
      marginType
      discount
      patientDiscountIncrements
      profitMarginIncrements
      formattedCurrentBalance
      formattedPayoutDate
      has1099kCapability
      metricsForRange {
        patientsCount
      }
      bankAccounts {
        id
        bankName
        accountNumber
        currentState
      }
      merchantData {
        id
        createdAt
        updatedAt
        accountType
        classification
        birthDay
        birthMonth
        birthYear
        businessName
        businessNumber
        city
        email
        employerId
        firstName
        isEquityOwner
        jobTitle
        lastName
        lastVerifiedTaxInformationAt
        phone
        postalCode
        region
        streetAddress
        streetAddress2
        businessAddress {
          id
          address1
          address2
          city
          state
          zipcode
          phone
        }
        invoiceAddress {
          id
          address1
          address2
          city
          state
          zipcode
        }
      }
      equityOwners {
        id
        firstName
        lastName
        streetAddress
        streetAddress2
        city
        region
        isDirector
        dateOfBirth
        postalCode
      }
      directors {
        id
        firstName
        lastName
        email
      }
      stripeAccountState {
        id
        isFullSsnRequired
        legalEntityVerified
        transfersEnabled
        pendingVerificationFields
        accountVerifyFieldsNeeded
        futureAccountFieldsNeeded
        additionalVerificationState
      }
      storeAdditionalDiscounts {
        id
        discountType
        discountValue
      }
    }
  }
`;

type MerchantDataSplinter = Pick<
  MerchantData,
  | "id"
  | "createdAt"
  | "updatedAt"
  | "accountType"
  | "classification"
  | "birthDay"
  | "birthMonth"
  | "birthYear"
  | "businessName"
  | "businessNumber"
  | "city"
  | "employerId"
  | "firstName"
  | "lastName"
  | "lastVerifiedTaxInformationAt"
  | "phone"
  | "postalCode"
  | "region"
  | "streetAddress"
  | "streetAddress2"
  | "jobTitle"
  | "isEquityOwner"
  | "email"
  | "businessAddress"
  | "invoiceAddress"
>;

type StoreMetricsSplinter = Pick<StoreMetrics, "patientsCount">;
type StoreAdditionalDiscountSplinter = Pick<
  StoreAdditionalDiscount,
  "id" | "discountType" | "discountValue"
>;

interface BankAccount {
  id: string;
  bankName: string;
  accountNumber: string;
  currentState: string;
}

interface StripeAccountState {
  id: string;
  isFullSsnRequired: boolean;
  legalEntityVerified: "verified" | "unverified" | "pending" | null;
  transfersEnabled: boolean;
  accountVerifyFieldsNeeded: string;
  pendingVerificationFields: string;
  futureAccountFieldsNeeded?: string;
  additionalVerificationState: StripeAdditionalVerificationStates;
}

interface PayoutDetailsData {
  currentPractitioner: {
    id: string;
    properties: {
      id: string;
      isStudent: boolean;
    };
  };
  currentStore: {
    id: string;
    profitMargin: number;
    marginType: MarginType;
    discount: number;
    patientDiscountIncrements: number[];
    profitMarginIncrements: number[];
    formattedCurrentBalance: string;
    formattedPayoutDate: string;
    has1099kCapability: boolean;
    bankAccounts: BankAccount[];
    merchantData: MerchantDataSplinter;
    stripeAccountState: StripeAccountState;
    metricsForRange: StoreMetricsSplinter;
    storeAdditionalDiscounts: StoreAdditionalDiscountSplinter[];
    equityOwners?: StripeAccountPersonType[];
    directors?: StripeAccountPersonType[];
  };
}

const usePayoutDetailsQuery = (options?: QueryHookOptions<PayoutDetailsData>) =>
  useQuery<PayoutDetailsData>(PayoutDetails_Shared_Query, options);

export type { PayoutDetailsData, BankAccount, MerchantDataSplinter, StripeAccountState };

/* eslint-disable @fullscript/gql-no-manual-hook-declaration */
export { usePayoutDetailsQuery, PayoutDetails_Shared_Query };
